import {Controller} from "@hotwired/stimulus"

const VIDEO_SELECTOR = '[data-field-id="raw_video"] video'
const MEDIA_EVENTS = ["loadedmetadata", "loadeddata", "resize", "error", "emptied", "loadstart"]

// Avo previews the original upload, which may contain a codec such as ProRes
// that Chrome cannot display even though its audio plays. Detect missing video
// or decoding errors and suggest Safari on macOS or downloading the original.
// Check playback rather than the file extension: MOV files can also contain
// browser-compatible H.264 video and do not always need a warning.
export default class extends Controller {
  connect() {
    this.onMediaEvent = this.handleMediaEvent.bind(this)

    // Media events do not bubble. Capture also handles players added when an
    // Avo tab or upload field is replaced through Turbo.
    MEDIA_EVENTS.forEach(event => this.element.addEventListener(event, this.onMediaEvent, true))
    this.element.querySelectorAll(VIDEO_SELECTOR).forEach(video => this.updateWarning(video))
  }

  disconnect() {
    MEDIA_EVENTS.forEach(event => this.element.removeEventListener(event, this.onMediaEvent, true))
  }

  handleMediaEvent(event) {
    const video = event.target
    if (!video.matches(VIDEO_SELECTOR)) return

    this.updateWarning(video, ["emptied", "loadstart"].includes(event.type))
  }

  updateWarning(video, reset = false) {
    const warning = video.parentElement.querySelector(".raw-video-preview-warning")

    if (reset || !this.previewUnavailable(video)) {
      warning?.remove()
      return
    }

    if (warning) return

    const message = document.createElement("p")
    message.className = "raw-video-preview-warning"
    message.setAttribute("role", "status")
    message.textContent = "This browser could not display the video preview. Try opening this page in Safari on macOS, or download the original file."
    video.insertAdjacentElement("afterend", message)
  }

  previewUnavailable(video) {
    // Network failures and cancelled loads do not indicate codec support.
    if (video.error) return [MediaError.MEDIA_ERR_DECODE, MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED].includes(video.error.code)

    // Unsupported video tracks can still load and play their audio. Wait for
    // metadata before checking dimensions: every unloaded player starts at 0.
    return video.readyState >= HTMLMediaElement.HAVE_METADATA && (video.videoWidth === 0 || video.videoHeight === 0)
  }
}
